import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Col,
  Form,
  Row,
  Space,
  TreeSelect as AntTreeSelect,
  Typography,
} from 'antd'
import { get, includes, isEmpty, isNil, map, omit, size } from 'lodash'
import PropTypes from 'prop-types'

import { SIZES } from '../../constants'
import { handleTreeSelectDropdownSearchHighlights } from '../../helpers'
import { antFormItemNamePropType } from '../../prop-types'
import { formatFormItemLabel, formatFormListLabel } from '../../utils'

import ListItemTreeSelect from './list-item-tree-select'

const FormLazyListTreeSelect = ({
  treeData,
  name,
  label,
  showCountItems,
  newItemTreeSelectPlaceholder,
  form,
  rules,
  itemRules,
  isDirty,
  showColon,
  labelAbove,
  itemRender,
  groupedFlatTreeDataByValue,
  initialValue,
  ...rest
}) => {
  const [lazyTreeData, setLazyTreeData] = useState([])
  const [listSize, setListSize] = useState(size(form.getFieldValue(name)))

  const initialTreeData = useMemo(
    () =>
      map(initialValue, value =>
        omit(get(groupedFlatTreeDataByValue, `${value}[0]`), 'children'),
      ),
    [groupedFlatTreeDataByValue, initialValue],
  )

  const treeDataToUse = useMemo(
    () => (isEmpty(lazyTreeData) ? initialTreeData : lazyTreeData),
    [initialTreeData, lazyTreeData],
  )

  const labelContent = useMemo(() => {
    const formListLabel = formatFormListLabel(label, listSize, showCountItems)
    return formatFormItemLabel(formListLabel, showColon)
  }, [listSize, label, showColon, showCountItems])

  const shouldModify = useCallback(
    value => {
      const existingValues = form.getFieldValue(name)
      return !includes(existingValues, value)
    },
    [form, name],
  )

  const handleAddItemWithValue = useCallback(
    add => value => {
      if (shouldModify(value)) {
        add(value)
        setListSize(previousSize => previousSize + 1)
      }
    },
    [shouldModify],
  )
  const handleRemoveItemByName = useCallback(
    remove => name => {
      remove(name)
      setListSize(previousSize => previousSize - 1)
    },
    [],
  )

  const handleOnFocusTreeSelectItem = useCallback(
    () => setLazyTreeData(treeData),
    [treeData],
  )

  const listItemRender = useCallback(
    ({ key, name, fieldKey, ...restField }, remove) => {
      if (!isNil(itemRender)) {
        return itemRender({
          field: { key, name, fieldKey, ...restField },
          shouldModify,
          treeData: treeDataToUse,
          onClear: handleRemoveItemByName(remove),
          onFocus: handleOnFocusTreeSelectItem,
          itemRules,
          ...rest,
        })
      }

      return (
        <Form.Item
          key={key}
          name={name}
          fieldKey={fieldKey}
          rules={itemRules}
          noStyle={isEmpty(itemRules)}
          {...restField}
        >
          <ListItemTreeSelect
            shouldModify={shouldModify}
            name={name}
            treeData={treeDataToUse}
            onClear={handleRemoveItemByName(remove)}
            onFocus={handleOnFocusTreeSelectItem}
            {...rest}
          />
        </Form.Item>
      )
    },
    [
      itemRender,
      itemRules,
      shouldModify,
      treeDataToUse,
      handleRemoveItemByName,
      handleOnFocusTreeSelectItem,
      rest,
    ],
  )

  useEffect(() => {
    setListSize(size(form.getFieldValue(name)))
  }, [form, isDirty, name])

  return (
    <Row align="middle">
      {labelAbove ? (
        <Col className="ant-form-item-label" span={24}>
          <Typography.Paragraph style={{ height: '32px', margin: 0 }}>
            {labelContent}
          </Typography.Paragraph>
        </Col>
      ) : (
        <Col span={3}>
          <Typography.Text>{labelContent}</Typography.Text>
        </Col>
      )}
      <Col span={labelAbove ? 24 : 21}>
        <Form.List rules={rules} label={label} name={name}>
          {(fields, { add, remove }, { errors }) => (
            <>
              <Space wrap>
                {map(fields, field => listItemRender(field, remove))}
                <Form.Item noStyle={isEmpty(itemRules)}>
                  <AntTreeSelect
                    onChange={handleAddItemWithValue(add)}
                    placeholder={newItemTreeSelectPlaceholder}
                    treeData={
                      isEmpty(lazyTreeData) ? initialTreeData : lazyTreeData
                    }
                    value={null}
                    onFocus={handleOnFocusTreeSelectItem}
                    dropdownMatchSelectWidth={SIZES.DROPDOWN_MIN_WIDTH}
                    dropdownRender={handleTreeSelectDropdownSearchHighlights}
                    treeNodeFilterProp="title"
                    {...rest}
                  />
                </Form.Item>
              </Space>
              <Row>
                <Form.ErrorList errors={errors} />
              </Row>
            </>
          )}
        </Form.List>
      </Col>
    </Row>
  )
}

FormLazyListTreeSelect.propTypes = {
  name: antFormItemNamePropType.isRequired,
  label: PropTypes.string,
  showCountItems: PropTypes.bool,
  newItemTreeSelectPlaceholder: PropTypes.string,
  treeData: PropTypes.array.isRequired,
  rules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  itemRules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  form: PropTypes.object.isRequired,
  isDirty: PropTypes.bool,
  showColon: PropTypes.bool,
  labelAbove: PropTypes.bool,
  itemRender: PropTypes.func,
  groupedFlatTreeDataByValue: PropTypes.object,
  initialValue: PropTypes.arrayOf(PropTypes.string),
}

FormLazyListTreeSelect.defaultProps = {
  label: '',
  showCountItems: false,
  newItemTreeSelectPlaceholder: 'Select a new item...',
  rules: undefined,
  itemRules: undefined,
  isDirty: false,
  showColon: false,
  labelAbove: true,
  itemRender: undefined,
  groupedFlatTreeDataByValue: {},
  initialValue: [],
}

export default FormLazyListTreeSelect
